import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import ThumbnailGenerator from '@uppy/thumbnail-generator';
import { ACCEPT_ARRAY, MAX_FILE_SIZE_BYTES } from '@optidev/shared-types';
import { auth } from '../firebase';
import { useEmbedStore } from '@/stores/embed-store';
import { getActiveWorkspaceId } from '@/stores/workspace-store';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface UppyUploadOptions {
  userMessage: string;
  source: 'chat' | 'editor' | 'visual-builder' | 'asset-manager' | 'img-gen' | 'builder';
  projectId?: string;
  sessionId?: string;
  messageId?: string;
  devContainerId?: string;
  onUploadStart?: (file: any) => void;
  onProgress?: (file: any, progress: number) => void;
  onComplete?: (file: any, result: any) => void;
  onError?: (file: any, error: any) => void;
}

export function createFileUploader(options: UppyUploadOptions) {
  const uppy = new Uppy({
    restrictions: {
      maxFileSize: MAX_FILE_SIZE_BYTES,
      allowedFileTypes: ACCEPT_ARRAY,
    },
    autoProceed: false,
  });

  // Add thumbnail generator for images (250x250)
  uppy.use(ThumbnailGenerator, {
    thumbnailWidth: 250,
    thumbnailHeight: 250,
    thumbnailType: 'image/jpeg',
    waitForThumbnailsBeforeUpload: true,
  });

  uppy.use(AwsS3, {
    shouldUseMultipart: false,
    getUploadParameters: async (file) => {
      try {
        // Get auth token
        const token = await auth.getIdToken();
        if (!token) {
          throw new Error('User not authenticated');
        }

        // Request presigned URL from backend
        // In embed mode the user's default workspaceId in DB (set during /api/auth/join
        // before provisioning) differs from the active workspace (from ?ws= URL param).
        // Without this header the file is created under the wrong workspace, and
        // buildAugmentedPrompt can't find it (queries by project.workspaceId).
        const headers: Record<string, string> = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        };
        if (useEmbedStore.getState().isEmbedded) {
          const wsId = getActiveWorkspaceId();
          if (wsId) headers['X-Workspace-Id'] = wsId;
        }

        const response = await fetch(`${API_URL}/api/files/presigned-url`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            fileSize: file.size,
            userMessage: options.userMessage,
            source: options.source,
            projectId: options.projectId,
            sessionId: options.sessionId,
            messageId: options.messageId,
            devContainerId: options.devContainerId,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to get presigned URL');
        }

        const { uploadUrl, fileId, refId, uploadKey, intent } = await response.json();

        console.log('[Uppy] Got presigned URL:', { uploadUrl: uploadUrl.substring(0, 100), fileId, intent });

        // Store metadata in file for later use
        file.meta.fileId = fileId;
        file.meta.refId = refId;
        file.meta.uploadKey = uploadKey;
        file.meta.intent = intent;

        if (options.onUploadStart) {
          options.onUploadStart(file);
        }

        return {
          method: 'PUT',
          url: uploadUrl,
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
          fields: {}, // Empty fields for presigned URL
        };
      } catch (error: any) {
        console.error('Failed to get presigned URL:', error);
        if (options.onError) {
          options.onError(file, error);
        }
        throw error;
      }
    },
  });

  // Progress tracking
  uppy.on('upload-progress', (file, progress) => {
    if (file && options.onProgress && progress.bytesTotal) {
      const percentage = (progress.bytesUploaded / progress.bytesTotal) * 100;
      options.onProgress(file, percentage);
    }
  });

  // Upload success - notify backend
  uppy.on('upload-success', async (file) => {
    if (!file) return;

    try {
      // Get auth token
      const token = await auth.getIdToken();
      if (!token) {
        throw new Error('User not authenticated');
      }

      // Notify backend that upload is complete
      const completeResponse = await fetch(`${API_URL}/api/files/upload-complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileId: file.meta.fileId,
          uploadKey: file.meta.uploadKey,
          fileSize: file.size, // Send actual file size
        }),
      });

      if (!completeResponse.ok) {
        const error = await completeResponse.json();
        throw new Error(error.message || 'Failed to mark upload complete');
      }

      const result = await completeResponse.json();

      console.log('[Uppy] Upload complete response:', result);

      // Upload thumbnail if it exists (for images only)
      let thumbnailUrl = null;
      if (file.preview && file.type?.startsWith('image/')) {
        try {
          console.log('[Uppy] Uploading thumbnail for', file.name);

          // Convert base64 preview to blob
          const response = await fetch(file.preview);
          const blob = await response.blob();

          // Get presigned URL for thumbnail (just for upload, not a separate file record).
          // Send the actual blob size — backend signs the URL for that exact size,
          // and R2 enforces exact-match on ContentLength via SigV4.
          const thumbResponse = await fetch(`${API_URL}/api/files/${file.meta.fileId}/thumbnail-upload`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ size: blob.size }),
          });

          if (thumbResponse.ok) {
            const thumbData = await thumbResponse.json();

            // Upload thumbnail to R2
            const putResp = await fetch(thumbData.uploadUrl, {
              method: 'PUT',
              headers: { 'Content-Type': 'image/jpeg' },
              body: blob,
            });

            if (!putResp.ok) {
              const body = await putResp.text();
              throw new Error(`Thumbnail PUT to R2 failed: ${putResp.status} ${body}`);
            }

            thumbnailUrl = thumbData.thumbnailUrl;
            file.meta.thumbnailUrl = thumbnailUrl;
          } else {
            const body = await thumbResponse.text();
            throw new Error(`Failed to get thumbnail upload URL: ${thumbResponse.status} ${body}`);
          }
        } catch (error) {
          console.error('[Uppy] Thumbnail upload failed:', error);
          // Non-fatal error, continue with original file
        }
      }

      // Store download URL and metadata for Claude
      file.meta.downloadUrl = result.file.downloadUrl;
      file.meta.filename = result.file.filename;
      file.meta.assetUrl = result.file.assetUrl;

      // Ensure thumbnailUrl is set (may have been set earlier)
      if (!file.meta.thumbnailUrl) {
        file.meta.thumbnailUrl = thumbnailUrl;
      }

      console.log('[Uppy] Stored in meta:', {
        downloadUrl: file.meta.downloadUrl,
        filename: file.meta.filename,
        assetUrl: file.meta.assetUrl,
      });

      // Mark that upload-complete has finished
      file.meta.uploadCompleteFinished = true;

      if (options.onComplete) {
        options.onComplete(file, {
          fileId: result.file.id,
          refId: result.file.refId,
          assetUrl: result.file.assetUrl,
          downloadUrl: result.file.downloadUrl,
          filename: result.file.filename,
          intent: result.file.intent,
          status: result.file.status,
          thumbnailUrl,
        });
      }
    } catch (error: any) {
      console.error('Failed to mark upload complete:', error);
      if (options.onError) {
        options.onError(file, error);
      }
    }
  });

  // Upload error
  uppy.on('upload-error', (file, error) => {
    console.error('Upload error:', error);
    if (file && options.onError) {
      options.onError(file, error);
    }
  });

  return uppy;
}

export function destroyUploader(uppy: Uppy) {
  uppy.cancelAll();
  // @ts-expect-error - close method exists but not in types
  if (typeof uppy.close === 'function') {
    // @ts-expect-error
    uppy.close();
  }
}
